import type {
  GoalSummary,
  HomeData,
  Mind,
  PlatformRunnerState,
  ProjectLiveSummary,
  ProjectSummary,
  RunnerListItemV1,
  WorkspaceRole,
} from "@minga/protocol"
import { cloudWhere, progressText, thinksWhere, WORDS } from "@/model/glossary"
import { leadStatus } from "@/model/lead-status"
import {
  blocksLead,
  cliOfMind,
  type LeadReach,
  leadReach,
  reachRunnersOf,
  type SetupProblem,
  setupProblems,
  workspaceKeyOf,
} from "@/model/setup"

/** Pure parts of the workspace home (settings wave §3.4, earlier spec §3, UX §5). */

export interface ChecklistItem {
  readonly key: "runner" | "connect" | "project" | "repo" | "invite" | "key" | "hi" | "subscription" | "phone"
  readonly label: string
  readonly done: boolean
  readonly optional: boolean
  readonly hint?: string
  /** "hi": the project whose lead to greet. */
  readonly projectId?: string
}

/** What only the browser knows, for a member's steps (decision 0130). */
export interface ChecklistContext {
  /** Minga runs as an installed app (or was installed during this visit). */
  readonly installed?: boolean
  /**
   * Onboarding 2026-10-23 §5: how many push devices the person registered (null while loading or unreadable). The phone
   * step is done on a registered device, not on the install alone (an iOS Home Screen app is signed out at first).
   */
  readonly pushDevices?: number | null
  /**
   * Onboarding 2026-10-23 §6: the Platform Runner's subscriptions as they concern the person: their own (`mine`) and
   * teammates' shared with the team (`team`). null while loading, unreadable or not set up.
   */
  readonly subscriptions?: { readonly mine: number; readonly team: number } | null
  /**
   * Onboarding week A9: someone set up the workspace's Platform Runner (null while loading or unknown). A member's
   * "Connect your own Codex subscription" leads to a working step only then; before, it is not offered.
   */
  readonly platformReady?: boolean | null
  /**
   * Decision 0166 §1.2: one of the person's own paired runners (not the Platform Runner) is online with a signed-in
   * Claude Code or Codex login, so it can run their lead (null while loading). "Run on your own machine" ticks only then.
   */
  readonly pairedRunnerReady?: boolean | null
}

/**
 * Decision 0166 §1.2: a paired runner of the person's that can run their lead now: theirs, not the Platform Runner,
 * online, with an enabled and signed-in Claude Code or Codex profile.
 */
export const pairedRunnerReady = (
  runners: ReadonlyArray<{
    readonly runnerId: string
    readonly ownerUserId: string
    readonly status: string
    readonly profiles: ReadonlyArray<{
      readonly agent: string
      readonly enabled: boolean
      readonly loggedIn: boolean
    }>
  }> | null,
  userId: string,
  platformRunnerId: string | null,
): boolean | null =>
  runners === null
    ? null
    : runners.some(
        (r) =>
          r.ownerUserId === userId &&
          r.runnerId !== platformRunnerId &&
          r.status === "online" &&
          r.profiles.some((p) => p.enabled && p.loggedIn && (p.agent === "claude" || p.agent === "codex")),
      )

/** The viewer's own Platform Runner subscriptions and teammates' Team ones (decision 0128), for the member checklist. */
export const subscriptionCounts = (
  state: Pick<PlatformRunnerState, "configured" | "ownerUserId" | "subscriptions"> | null | undefined,
  userId: string,
): { readonly mine: number; readonly team: number } | null => {
  if (!state?.configured) return null
  const ownerOf = (s: PlatformRunnerState["subscriptions"][number]) => s.ownerUserId ?? state.ownerUserId
  return {
    mine: state.subscriptions.filter((s) => !s.redacted && ownerOf(s) === userId).length,
    team: state.subscriptions.filter((s) => s.scope === "team" && ownerOf(s) !== userId).length,
  }
}

/**
 * Decision 0166 §1.2: "Connect your subscription", step 1 for owners and members alike. Done once the person has a
 * subscription of their own on the Platform Runner, a teammate's shared one runs their work (as 0153), or one of their
 * own runners can run their lead. Open while the subscriptions are unknown.
 */
export const subscriptionStep = (ctx: ChecklistContext): ChecklistItem => {
  const subs = ctx.subscriptions ?? null
  const done = (subs !== null && (subs.mine > 0 || subs.team > 0)) || ctx.pairedRunnerReady === true
  return {
    key: "subscription",
    label: "Connect your subscription",
    done,
    optional: false,
    ...(done ? {} : { hint: "Claude or Codex; it runs your lead on the Platform Runner" }),
  }
}

/** The project a member says hi in (decision 0130): the first active one they are in, else the first they may read. */
export const helloProjectOf = (home: HomeData): ProjectSummary | undefined => {
  const active = sortProjects(home.projects).shown.filter((p) => p.status === "active")
  return active.find((p) => p.access !== "reader") ?? active[0]
}

/**
 * "Get set up" (wave B2, decision 0057 §4.2; decision 0166 §1.2), in the order to do it: Connect your subscription ·
 * Start a project · Attach a repo (once a project exists) · Invite someone (owner/admin) · Optional: Run on your own
 * machine (ticked only when one of your runners can run your lead) · Use Minga from Claude Code or Codex · Add a model
 * key.
 * Items derive from real state, never from clicks. `connectedApps`: how many apps the viewer connected over MCP (null
 * while loading or unreadable: the step stays open).
 *
 * Decision 0130 (audit items 8, 12): a member joins a team that is set up already, so their steps are their own: say
 * hi to the lead in their project (done once they asked something there), and optionally connect their own Claude or
 * Codex subscription and install the app for notifications. No runner, no terminal, no model key.
 */
export const checklist = (
  home: HomeData,
  role: WorkspaceRole,
  connectedApps: number | null = null,
  ctx: ChecklistContext = {},
): ReadonlyArray<ChecklistItem> => {
  if (role === "member") {
    const member: ChecklistItem[] = []
    const hello = helloProjectOf(home)
    if (hello)
      member.push({
        key: "hi",
        // polish 2026-10-23 §4: a project they only read is joined by their first message ("Join … and say hi")
        label:
          hello.access === "reader"
            ? `Join ${hello.name} and say hi to ${hello.live?.lead?.name ?? "the lead"}`
            : `Say hi to ${hello.live?.lead?.name ?? "the lead"} in ${hello.name}`,
        done: home.projects.some((p) => p.live?.youWrote === true),
        optional: false,
        projectId: hello.id,
      })
    // decision 0166 §1.2: step 1 for members too; done once they own one, or a teammate's shared one runs their work
    member.unshift(subscriptionStep(ctx))
    member.push({
      key: "phone",
      label: "Install the app for notifications",
      done: (ctx.pushDevices ?? 0) > 0,
      optional: true,
      hint: "on your phone",
    })
    return member
  }
  // decision 0166 §1.2: a subscription first (it runs the lead on the Platform Runner); a runner of your own and Minga
  // in your terminal are optional
  const items: ChecklistItem[] = [
    subscriptionStep(ctx),
    { key: "project", label: WORDS.startProject, done: home.projects.length >= 1, optional: false },
  ]
  if (home.projects.length > 0)
    items.push({
      key: "repo",
      label: "Attach a repo",
      done: home.projects.some((p) => p.status === "active" && p.repoIds.length > 0),
      optional: false,
    })
  if (role === "owner" || role === "admin")
    items.push({
      key: "invite",
      label: "Invite someone",
      done: home.members.count >= 2 || (home.members.pendingInvitations ?? 0) >= 1,
      optional: false,
    })
  items.push(
    {
      key: "runner",
      label: "Run on your own machine",
      done: ctx.pairedRunnerReady === true,
      optional: true,
      hint: "pair a runner",
    },
    {
      key: "connect",
      label: "Use Minga from Claude Code or Codex",
      done: (connectedApps ?? 0) >= 1,
      optional: true,
    },
  )
  const keyDone = [home.modelKeys.anthropic, home.modelKeys.openai].some(
    (k) => k !== null && k !== "rejected",
  )
  items.push({
    key: "key",
    label: "Add a model key",
    done: keyDone,
    optional: true,
    hint: "only for a lead in the cloud",
  })
  return items
}

/**
 * Onboarding week A2: what Get set up still shows once every required step is done. The phone step stays (alone) until
 * the person has a push device, so the way to install the app and turn on notifications doesn't vanish with "Say hi".
 * Nothing once push is known to be off on this Minga, or while the device count is unknown.
 */
export const stepsAfterSetup = (
  items: ReadonlyArray<ChecklistItem>,
  ctx: Pick<ChecklistContext, "pushDevices"> & { readonly pushEnabled?: boolean | null },
): ReadonlyArray<ChecklistItem> =>
  ctx.pushEnabled === true && ctx.pushDevices === 0 ? items.filter((i) => i.key === "phone" && !i.done) : []

/**
 * Onboarding week A4 (jargon row 32): a project card without goals. An empty project asks what to build; one with a
 * plan (or a written overview) points to the chat instead, so a newcomer doesn't read it as "start over".
 */
export const goallessLine = (
  live: Pick<ProjectLiveSummary, "lead" | "planned" | "onboarded" | "chatted">,
): string => {
  const name = live.lead?.name ?? "the lead"
  // decision 0166 §5: a chat or a card already there is not "start over" either
  return live.planned === true || live.onboarded === true || live.chatted === true
    ? `Open the chat with ${name} →`
    : `Tell ${name} what you want to build →`
}

/**
 * Onboarding week A4: a member's Home doesn't lead with starting a project. The "Start a project" card shows for owners
 * and admins, and for a member who is in no project yet.
 */
export const showStartCard = (home: Pick<HomeData, "me" | "projects">): boolean =>
  home.me.role !== "member" || !home.projects.some((p) => p.access !== "reader")

/** "2 of 5" over the required items; hidden when all of them are done. */
export const checklistProgress = (
  items: ReadonlyArray<ChecklistItem>,
): { readonly done: number; readonly total: number; readonly complete: boolean } => {
  const required = items.filter((i) => !i.optional)
  const done = required.filter((i) => i.done).length
  return { done, total: required.length, complete: done === required.length }
}

/** Active projects first, then paused (both by name); archived ones separately. */
export const sortProjects = (
  projects: ReadonlyArray<ProjectSummary>,
): { readonly shown: ReadonlyArray<ProjectSummary>; readonly archived: ReadonlyArray<ProjectSummary> } => {
  const byName = (a: ProjectSummary, b: ProjectSummary) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  return {
    shown: [
      ...projects.filter((p) => p.status === "active").sort(byName),
      ...projects.filter((p) => p.status === "paused").sort(byName),
    ],
    archived: projects.filter((p) => p.status === "archived").sort(byName),
  }
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** `{areas} areas · {running} running · {workers} workers · {blocked} blocked · {n} waiting for a runner`, zeros omitted. */
export const countsLine = (counts: ProjectLiveSummary["counts"]): string =>
  [
    counts.areas ? plural(counts.areas, "area") : null,
    counts.running ? `${counts.running} running` : null,
    counts.temporaryWorkers ? plural(counts.temporaryWorkers, "worker") : null,
    counts.blocked ? `${counts.blocked} blocked` : null,
    counts.waitingForRunner ? `${counts.waitingForRunner} waiting for a runner` : null,
  ]
    .filter(Boolean)
    .join(" · ")

/** Where Lea thinks, for the card's tooltip (glossary): "Thinks on jannik-mbp", "Thinks in the cloud (Anthropic key)". */
export const leadWhere = (mind: Mind, runnerName: (id: string) => string): string =>
  mind._tag === "runner"
    ? mind.requires.runner
      ? `Thinks on ${runnerName(mind.requires.runner)}`
      : "Thinks on any of your runners"
    : thinksWhere(cloudWhere(mind.provider), true)

/**
 * Lea's status on a project card (§4.2), from `leaStatusWord`: "Lea is free", "Lea is working", "Lea can't work",
 * "Lea is waiting for a runner". Never the machine or the model (those are in the tooltip, `leadWhere`). `blocked`: a
 * setup problem keeps her from thinking (`projectProblems`), so she is never "idle" without a key.
 */
export const leadLine = (
  lead: Pick<NonNullable<ProjectLiveSummary["lead"]>, "name" | "state" | "lastTurnFailed">,
  blocked = false,
): string =>
  // decision 0168: the same status as the project's chip and header (`leadStatus`)
  leadStatus({
    name: lead.name,
    state: lead.state,
    problems: blocked ? [{ _tag: "no_runner", lead: true }] : [],
    lastTurnFailed: lead.lastTurnFailed === true,
  }).sentence

/** The kind tag of a Home Needs-you row (`kind` is the card body's tag; Home has no state): the glossary's words. */
export const kindWord = (kind: string): string => {
  switch (kind) {
    case "plan_change":
      return WORDS.kinds.plan
    case "question":
      return WORDS.kinds.question
    case "permission":
      return WORDS.kinds.permission
    case "memory":
      return WORDS.kinds.memory
    case "outward_action":
      return WORDS.kinds.outward
    default:
      return WORDS.verbs.decide
  }
}

type NeedsRow = HomeData["needsYou"]["items"][number]

const CARD_WORDS: Readonly<Record<string, string>> = {
  plan: WORDS.kinds.plan,
  brief: WORDS.kinds.briefEdit,
  stuck: WORDS.kinds.stuck,
  review: WORDS.kinds.review,
  question: WORDS.kinds.question,
  permission: WORDS.kinds.permission,
  memory: WORDS.kinds.memory,
  outward: WORDS.kinds.outward,
}

/** A Home row's kind tag: the server's card tag in the card's words ("Stuck", "Review"), else the body's tag. */
export const rowKindWord = (row: Pick<NeedsRow, "kind" | "card" | "oneLine">): string =>
  (row.card ? CARD_WORDS[row.card] : undefined) ?? (legacyStuck(row) ? WORDS.kinds.stuck : kindWord(row.kind))

/** An older server's stuck card: "LEA-9 needs you: non retryable" (the ref, then the raw cause). */
const legacyStuck = (row: Pick<NeedsRow, "kind" | "oneLine">): RegExpExecArray | null =>
  row.kind === "question" ? /^([A-Z][A-Z0-9]*-\d+) needs you: /.exec(row.oneLine) : null

/**
 * A Home row's words: the server's title (built with the card rules), else the stored one-line title. An older
 * server's stuck card ("LEA-9 needs you: non retryable") reads "LEA-9 is stuck" without the raw cause.
 */
export const rowTitle = (row: Pick<NeedsRow, "kind" | "oneLine" | "title">): string => {
  if (row.title) return row.title
  const stuck = legacyStuck(row)
  return stuck ? `${stuck[1]} is stuck` : row.oneLine
}

/**
 * The Needs-you count on Home: the cards listed, or the projects' own counts summed (their questions, items ready for
 * the person, stuck items, items to sort) when that is more, so Home never says "Nothing needs you" while a project's
 * tab shows a number.
 */
export const homeNeedsCount = (home: HomeData): number =>
  Math.max(
    home.needsYou.count,
    home.projects.filter((p) => p.status === "active").reduce((n, p) => n + (p.live?.needsYou ?? 0), 0),
  )

/**
 * Per project, what waits beyond the cards and items Home lists: "1 more". Decision 0163: the items whose move is the
 * person's are listed (`needsYou.tasks`), so this is only what the 50-row lists cut off (or an older server's items).
 */
export const moreInProjects = (
  home: HomeData,
): ReadonlyArray<{ readonly projectId: string; readonly projectName: string; readonly more: number }> =>
  home.projects
    .filter((p) => p.status === "active" && p.live !== null)
    .map((p) => ({
      projectId: p.id as string,
      projectName: p.name,
      more:
        (p.live?.needsYou ?? 0) -
        home.needsYou.items.filter((i) => i.projectId === p.id).length -
        (home.needsYou.tasks ?? []).filter((t) => t.projectId === p.id).length,
    }))
    .filter((x) => x.more > 0)

type TaskRow = NonNullable<HomeData["needsYou"]["tasks"]>[number]

/** Decision 0163: a Home row's tag for an item whose move is the person's, in the Needs-you tab's words. */
export const taskKindWord = (row: Pick<TaskRow, "why">): string => {
  switch (row.why) {
    case "close":
      return "To close"
    case "answer":
      return WORDS.kinds.question
    case "stuck":
      return WORDS.kinds.stuck
    case "lend":
      return "Lend"
    case "sort":
      return WORDS.verbs.sort
    default:
      return "To do"
  }
}

/**
 * One project's setup problems from HomeData alone (its lead through `setupProblems`). A runner-minded lead that can't
 * work for another reason is left out (Home can't tell why); a cloud lead on the workspace key is checked against that
 * provider's key whatever her state, so she never reads "idle" without one.
 */
/**
 * Decision 0166 §1.1: the Platform Runner starts on demand, so once it has a subscription it counts as online for "can
 * the lead work" (it is listed "offline" while no sandbox runs). `platform`: its id and whether it has a subscription.
 */
export const withPlatformAwake = <R extends { readonly runnerId: string; readonly status: string }>(
  runners: ReadonlyArray<R> | undefined,
  platform:
    | { readonly runnerId: string | null; readonly subscriptions: ReadonlyArray<unknown> }
    | null
    | undefined,
): ReadonlyArray<R> | undefined =>
  runners && platform?.runnerId && platform.subscriptions.length > 0
    ? runners.map((r) => (r.runnerId === platform.runnerId ? { ...r, status: "online" } : r))
    : runners

/** Decision 0168: what Home reads the viewer's reach from (the project page's facts, per project). */
export interface HomeReachFacts {
  readonly viewerId: string
  readonly runners: ReadonlyArray<RunnerListItemV1>
  readonly platform: PlatformRunnerState | null
  /** The viewer's choice of subscriptions in a project (null: all they may use). */
  readonly chosenIn: (projectId: string) => ReadonlyArray<string> | null
}

/**
 * Decision 0168: could a turn this viewer asks the project's lead start now (core `leadReach`, as the project page
 * reads it), from the summary's lead (its mind and keeper). Null when unknown (an older server, a cloud lead).
 */
export const homeReach = (p: ProjectSummary, facts: HomeReachFacts | null | undefined): LeadReach | null => {
  const lead = p.live?.lead
  if (!facts || !lead?.ownerId || lead.mind._tag !== "runner") return null
  const mind = lead.mind
  return leadReach({
    keeperId: lead.ownerId,
    viewerId: facts.viewerId,
    agent: mind.agent,
    pinnedRunner: mind.requires.runner ?? null,
    pinnedProfile: mind.profileId ?? mind.requires.profile ?? null,
    runners: reachRunnersOf(facts.runners, facts.platform),
    projectId: p.id,
    chosen: facts.chosenIn(p.id),
  })
}

export const projectProblems = (
  home: HomeData,
  p: ProjectSummary,
  /** Polish 2026-10-23 §5: the workspace's runners when loaded, so a paired runner that sleeps reads "offline". */
  runners?: ReadonlyArray<{ readonly runnerId: string; readonly status: string }>,
  /** Decision 0168: the viewer's reach (`homeReach`), so Home says "can't work" when the chat does. */
  reach?: LeadReach | null,
  /** Decision 0168: the Platform Runner's id (a lead on it starts on demand, never "asleep"). */
  platformRunnerId?: string | null,
): ReadonlyArray<SetupProblem> => {
  const lead = p.live?.lead ?? null
  const counts = p.live?.counts
  const runnerMind = lead?.mind._tag === "runner"
  const noKey = [home.modelKeys.anthropic, home.modelKeys.openai].every((k) => k === null)
  const pinned = lead ? (p.live?.pinned?.find((x) => x.agentId === lead.agentId)?.runnerId ?? null) : null
  return setupProblems({
    lead: lead
      ? {
          name: lead.name,
          state: lead.state === "needs_attention" && runnerMind ? "idle" : lead.state,
          reason: null,
          mind: runnerMind ? "runner" : "cloud",
          runnerId: pinned,
          cli: cliOfMind(lead.mind),
          ...(pinned !== null && pinned === platformRunnerId ? { hosted: true } : {}),
        }
      : null,
    reach: reach ?? null,
    lastTurnError: noKey ? "no_api_key" : null,
    workspaceKey: workspaceKeyOf(lead?.mind, home.modelKeys),
    runners: runners
      ? {
          total: runners.length,
          online: runners.filter((r) => r.status === "online").length,
          onlineIds: runners.filter((r) => r.status === "online").map((r) => r.runnerId),
          offlineIds: runners.filter((r) => r.status !== "online").map((r) => r.runnerId),
        }
      : { total: home.runners.total, online: home.runners.online },
    waitingForRunner: counts?.waitingForRunner ?? 0,
  })
}

/**
 * What keeps the workspace's leads or work from moving (the Home strip, §4.2): each live project's problems, merged.
 * With several projects the strip names the one whose lead can't work ("Lea in Dictly"), or how many.
 */
export const homeSetup = (
  home: HomeData,
  runners?: ReadonlyArray<{ readonly runnerId: string; readonly status: string }>,
  /** Decision 0168: the viewer's reach per project (`homeReach`). */
  reachOf?: (p: ProjectSummary) => LeadReach | null,
  platformRunnerId?: string | null,
): {
  readonly problems: ReadonlyArray<SetupProblem>
  readonly leadName: string
  readonly waiting: number
} => {
  const live = home.projects.filter((p) => p.status === "active" && p.live !== null)
  const out: SetupProblem[] = []
  const blocked: ProjectSummary[] = []
  let waiting = 0
  for (const p of live) {
    waiting += p.live?.counts.waitingForRunner ?? 0
    const found = projectProblems(home, p, runners, reachOf?.(p) ?? null, platformRunnerId)
    if (found.some(blocksLead)) blocked.push(p)
    for (const f of found) if (!out.some((o) => o._tag === f._tag)) out.push(f)
  }
  const names = [...new Set(live.flatMap((p) => (p.live?.lead ? [p.live.lead.name] : [])))]
  const name = names.length === 1 ? (names[0] ?? "The lead") : "The lead"
  const leadOf = (p: ProjectSummary | undefined) => p?.live?.lead?.name ?? name
  const leadName =
    live.length <= 1
      ? name
      : blocked.length === 1
        ? `${leadOf(blocked[0])} in ${blocked[0]?.name ?? ""}`
        : blocked.length > 1
          ? `${name} in ${blocked.length} projects`
          : name
  return { problems: out, leadName, waiting }
}

/** A goal's numbers and progress (0–1) for a project card. */
export const goalProgress = (
  g: GoalSummary,
): { readonly text: string; readonly ratio: number; readonly due: number | null } =>
  g.kind === "metric"
    ? {
        text: `${g.current ?? "–"} / ${g.target}${g.unit ? ` ${g.unit}` : ""}`,
        ratio: g.current === null || g.target === 0 ? 0 : Math.min(1, Math.max(0, g.current / g.target)),
        due: null,
      }
    : {
        text: progressText(g.serving),
        ratio: g.serving.total === 0 ? 0 : Math.min(1, g.serving.done / g.serving.total),
        due: g.dueAt,
      }

/**
 * The greeting's sub-line parts: "jung.dev · 3 things need you · 2 of 3 runners online". Re-walk 2026-10-28: a member's
 * Home says nothing of runners (theirs are in the header pill once they pair one), and `runners`, when loaded, counts
 * the Platform Runner with a subscription as online (`withPlatformAwake`: it starts on demand).
 */
export const subLine = (
  home: HomeData,
  opts: {
    readonly role?: WorkspaceRole | undefined
    readonly runners?: ReadonlyArray<{ readonly status: string }> | undefined
  } = {},
): string => {
  const total = opts.runners ? opts.runners.length : home.runners.total
  const online = opts.runners ? opts.runners.filter((r) => r.status === "online").length : home.runners.online
  return [
    home.workspace.name,
    homeNeedsCount(home) > 0
      ? `${plural(homeNeedsCount(home), "thing")} need${homeNeedsCount(home) === 1 ? "s" : ""} you`
      : "Nothing needs you right now",
    opts.role !== "member" && total > 0 ? `${online} of ${total} runners online` : null,
  ]
    .filter(Boolean)
    .join(" · ")
}

// ---- decision 0098: the brief from each project's lead ----------------------------------------------------------

/** A lead's brief stays on Home this long; older ones are only in the bell. */
export const BRIEF_SHOWN_MS = 7 * 86_400_000

export interface HomeBrief {
  readonly project: ProjectSummary
  readonly brief: NonNullable<ProjectLiveSummary["brief"]>
  /** The viewer's Needs-you count in that project: what the lead needs from them, linked, not retyped. */
  readonly needsYou: number
}

/** The newest brief of each project's lead (not archived, at most a week old), newest first. */
export const homeBriefs = (home: HomeData, now: number): ReadonlyArray<HomeBrief> =>
  home.projects
    .flatMap((project) => {
      const brief = project.live?.brief
      if (project.status === "archived" || !brief || now - brief.at > BRIEF_SHOWN_MS) return []
      return [{ project, brief, needsYou: project.live?.needsYou ?? 0 }]
    })
    .sort((a, b) => b.brief.at - a.brief.at)
